/**
 * End-to-end payroll calculator — gross monthly salary → annual tax → monthly
 * net take-home.
 *
 * Pipeline:
 *   1. Annualize gross (months + festival bonus)
 *   2. Compute SSF/PF deductions
 *   3. Add other deductions (insurance, donation, retirement)
 *   4. Cap deductions per FY rules
 *   5. Apply slabs to taxable income
 *   6. Apply rebates (women, etc.)
 *   7. Annual net = gross − SSF/PF employee − tax
 *   8. Monthly net = annual net / 12
 *
 * @example
 * calculatePayroll({
 *   monthlyBasic: 50_000,
 *   monthlyAllowances: 20_000,
 *   festivalBonus: true,
 *   profile: { status: "single", isSSFMember: true },
 *   deductions: { lifeInsurance: 30_000 },
 * });
 */

import { calculateSSF, calculatePF } from "./contributions.js";
import { getRates } from "./data/index.js";
import { calculateIncomeTax } from "./income-tax.js";
import { totalDeductions } from "./tds.js";
import type {
  FiscalYear,
  PayrollResult,
  SalaryDeductions,
  TaxProfile,
} from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** Options for `calculatePayroll`. */
export interface PayrollOptions {
  /** Monthly basic salary (used as base for SSF/PF). */
  readonly monthlyBasic: number;
  /** Monthly non-basic allowances (taxable but not contribution-eligible). */
  readonly monthlyAllowances?: number;
  /** Whether a 1-month festival/Dashain bonus is paid (default true). */
  readonly festivalBonus?: boolean;
  /** Number of months (default 12 — Dashain bonus is added separately). */
  readonly months?: number;
  /** Tax profile. */
  readonly profile?: TaxProfile;
  /** Additional voluntary deductions. */
  readonly deductions?: SalaryDeductions;
  /** Use SSF (subtract employee SSF) or PF (default `"ssf"` if SSF member, else `"pf"`). */
  readonly retirementScheme?: "ssf" | "pf" | "none";
  /** Override PF employee rate (default 10%). */
  readonly pfEmployeeRate?: number;
  /** Override PF employer rate (default 10%). */
  readonly pfEmployerRate?: number;
  /** Fiscal year (default current). */
  readonly fiscalYear?: FiscalYear;
}

/**
 * Run a full payroll calculation. Returns the breakdown for one fiscal year.
 *
 * Conventions:
 *  - Festival bonus = 1 month basic + allowances, fully taxable.
 *  - Annual gross includes festival bonus.
 *  - Retirement scheme contribution counts as a deduction per `deductionCaps`.
 *  - SSF members are exempt from the 1% SST (handled in income-tax module).
 */
export function calculatePayroll(options: PayrollOptions): PayrollResult {
  const {
    monthlyBasic,
    monthlyAllowances = 0,
    festivalBonus = true,
    months = 12,
    profile = { status: "single" },
    deductions = {},
    retirementScheme,
    pfEmployeeRate,
    pfEmployerRate,
    fiscalYear,
  } = options;

  ensureNonNegative(monthlyBasic, "calculatePayroll");
  ensureNonNegative(monthlyAllowances, "calculatePayroll");

  const rates = getRates(fiscalYear);
  const notes: string[] = [];

  // 1. Annualize gross.
  const monthlyGross = monthlyBasic + monthlyAllowances;
  const baseAnnual = monthlyGross * months;
  const bonus = festivalBonus ? monthlyGross : 0;
  const annualGross = baseAnnual + bonus;
  const annualBasic = monthlyBasic * months + (festivalBonus ? monthlyBasic : 0);

  if (festivalBonus) notes.push(`Festival bonus: 1 month gross (Rs ${monthlyGross.toLocaleString()})`);

  // 2. Compute retirement scheme.
  const scheme = retirementScheme ?? (profile.isSSFMember ? "ssf" : "pf");
  let employeeRetirement = 0;
  let employerRetirement = 0;
  let ssfEmployee = 0;
  let ssfEmployer = 0;
  let pfEmployee = 0;
  let pfEmployer = 0;

  if (scheme === "ssf") {
    const ssf = calculateSSF(annualBasic, { fiscalYear });
    ssfEmployee = ssf.employee;
    ssfEmployer = ssf.employer;
    employeeRetirement = ssfEmployee;
    employerRetirement = ssfEmployer;
    notes.push("Retirement scheme: SSF (11% employee + 20% employer)");
  } else if (scheme === "pf") {
    const pf = calculatePF(annualBasic, {
      fiscalYear,
      ...(pfEmployeeRate !== undefined && { employeeRate: pfEmployeeRate }),
      ...(pfEmployerRate !== undefined && { employerRate: pfEmployerRate }),
    });
    pfEmployee = pf.employee;
    pfEmployer = pf.employer;
    employeeRetirement = pfEmployee;
    employerRetirement = pfEmployer;
    notes.push("Retirement scheme: PF (10% + 10%)");
  }

  // 3. Sum deductions (capped).
  // Add the employee retirement contribution as a deduction (counts toward
  // retirement cap), in addition to whatever the user passed in `deductions.retirement`.
  const totalRetirement = employeeRetirement + (deductions.retirement ?? 0);
  const cappedDeductions = totalDeductions(
    {
      retirement: totalRetirement,
      lifeInsurance: deductions.lifeInsurance ?? 0,
      healthInsurance: deductions.healthInsurance ?? 0,
      donation: deductions.donation ?? 0,
    },
    fiscalYear,
    profile.isSSFMember ?? false,
    annualGross,
  );

  // 4. Apply slabs.
  const taxResult = calculateIncomeTax({
    income: annualGross,
    status: profile.status,
    fiscalYear: rates.fiscalYear,
    profile,
    deductions: cappedDeductions,
  });
  notes.push(...taxResult.notes);

  // 5. Net = gross − employee retirement − tax. (Employer portion is not paid out to employee.)
  const annualNet = r2(annualGross - employeeRetirement - taxResult.tax);
  const monthlyNet = r2(annualNet / months);

  return {
    fiscalYear: rates.fiscalYear,
    annualGross: r2(annualGross),
    annualDeductions: cappedDeductions,
    taxableIncome: taxResult.taxableIncome,
    annualTax: taxResult.tax,
    monthlyTds: r2(taxResult.tax / months),
    annualNet,
    monthlyNet,
    ssfEmployee: r2(ssfEmployee),
    ssfEmployer: r2(ssfEmployer),
    pfEmployee: r2(pfEmployee),
    pfEmployer: r2(pfEmployer),
    notes,
  };
}
